// use-local-persistence.ts — device-local persistence concern for useLifeOs.
// Owns the storage-error flag, the immediate-write path (localStorage with an
// IndexedDB reference for large snapshots), and the debounced persist scheduler.

import { useCallback, useEffect, useRef, useState } from "react";
import {
  createLocalStateStorageKey,
  saveIndexedLocalStateReference,
  saveLocalState,
} from "../lib/storage";
import { saveIndexedState } from "../lib/indexeddb-storage";
import { createPersistScheduler } from "../lib/persist-scheduler";
import { INDEXED_REFERENCE_ITEM_THRESHOLD, persistedItemCount } from "./life-os-sync-internals";
import type { LifeOsState } from "../types";

export function useLocalPersistence() {
  // True when the last localStorage persist failed (quota/private mode).
  const [storageError, setStorageError] = useState(false);

  const persistStateNow = useCallback(
    (nextState: LifeOsState, storageKey = createLocalStateStorageKey()) => {
      if (persistedItemCount(nextState) >= INDEXED_REFERENCE_ITEM_THRESHOLD) {
        void saveIndexedState(storageKey, nextState).then((indexedSaved) => {
          const localSaved = indexedSaved
            ? saveIndexedLocalStateReference(nextState.updatedAt, storageKey)
            : saveLocalState(nextState, storageKey);
          setStorageError(!localSaved);
        });
        return true;
      }

      const saved = saveLocalState(nextState, storageKey);
      setStorageError(!saved);
      void saveIndexedState(storageKey, nextState);
      return saved;
    },
    [],
  );

  const persistScheduler = useRef<ReturnType<typeof createPersistScheduler<LifeOsState>>>();
  if (!persistScheduler.current) {
    persistScheduler.current = createPersistScheduler<LifeOsState>((storageKey, nextState) => {
      persistStateNow(nextState, storageKey);
    });
  }

  const schedulePersist = useCallback((storageKey: string, nextState: LifeOsState) => {
    persistScheduler.current?.schedule(storageKey, nextState);
  }, []);

  // Flush any pending debounced write on unmount.
  useEffect(() => {
    return () => {
      void persistScheduler.current?.flush();
    };
  }, []);

  return { persistStateNow, schedulePersist, storageError };
}
